import { useState } from "react";
import { CaretDown, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import MarketingLayout from "../components/MarketingLayout";

const FAQ_GROUPS = [
  {
    title: "Plans & pricing",
    qs: [
      {
        q: "Do you really offer a 14-day free trial?",
        a: "Yes — every plan (Basic, Team, Pro) includes a full 14-day free trial. We collect a card up front but you won't be charged until day 15. Cancel any time during the trial and you'll never be billed.",
      },
      {
        q: "What's the difference between Basic, Team and Pro?",
        a: "Basic ($29/mo) is built for solo operators and includes 2 users (1 admin + 1 viewer). Team ($59/mo) adds the Warehouse Staff role, end-of-day digest emails, and 5 users total. Pro ($89/mo) unlocks financial reports (margin, COGS, inventory valuation), CSV/PDF financial exports, priority email support and 10 users.",
      },
      {
        q: "Can I change plans later?",
        a: "Yes — click 'Manage plan' inside the app or 'Update billing' on the Billing page. Stripe handles the proration automatically, so you only pay the difference for the days remaining in your billing cycle.",
      },
      {
        q: "How do I cancel?",
        a: "Open the Billing page in the app, click 'Manage subscription' to launch the Stripe Customer Portal, and cancel from there. Your access continues until the end of the current billing period.",
      },
    ],
  },
  {
    title: "Features & usage",
    qs: [
      {
        q: "Can I scan barcodes with my phone?",
        a: "Yes — on the Team and Pro plans. ezItemizer works as a Progressive Web App (PWA): install it on your phone's home screen, then use the in-app camera scanner to scan barcodes or QR codes on parts. Works on iOS 16.4+ and modern Android. (Camera scanning is not included in the Basic plan.)",
      },
      {
        q: "What barcode formats are supported?",
        a: "We support every common 1D and 2D format: UPC-A, UPC-E, EAN-8, EAN-13, Code 39, Code 128, ITF, QR codes, and Data Matrix.",
      },
      {
        q: "How do user roles work?",
        a: "Admins can manage everything — users, categories, parts, transactions, and billing. Warehouse Staff (Team and Pro plans only) can manage parts and log stock movements but can't touch users, billing, or delete parts. Viewers have read-only access — perfect for accountants or anyone who needs to see stock levels but shouldn't change them.",
      },
      {
        q: "Can I import existing inventory?",
        a: "Yes — drop us a line at support@ezitemizer.com with your existing inventory (CSV is best) and we'll help you migrate during your trial. CSV self-import is on the roadmap.",
      },
    ],
  },
  {
    title: "Data & security",
    qs: [
      {
        q: "Is my data safe?",
        a: "Yes. All traffic is HTTPS-encrypted, passwords are bcrypt-hashed with a unique salt per user, and your data lives in encrypted MongoDB Atlas clusters. Each organization's data is fully scoped — no other ezItemizer customer can see your parts.",
      },
      {
        q: "Can I export my data?",
        a: "Anytime. The Reports page lets you export parts and transactions to CSV or PDF. Pro plans also get financial reports (inventory valuation, COGS, margin) as exports. Your data is always yours.",
      },
      {
        q: "What happens to my data if I cancel?",
        a: "We keep your inventory data for 30 days after cancellation in case you reactivate. After that we permanently delete it. Email support@ezitemizer.com if you want immediate deletion.",
      },
    ],
  },
  {
    title: "Support",
    qs: [
      {
        q: "How do I get help?",
        a: "Customers can use the in-app Feedback button (bottom-right corner) — it auto-attaches your account context so we can help faster. Prospective customers can email support@ezitemizer.com or use the Contact form on this site. We reply within 1 business day.",
      },
      {
        q: "Do you offer onboarding help?",
        a: "Pro customers get priority email support during onboarding. For Basic and Team plans, email support@ezitemizer.com and we'll help you get up and running.",
      },
    ],
  },
];

function FaqItem({ q, a, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-zinc-200 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-5 text-left group"
      >
        <span className="font-display font-bold text-base sm:text-lg text-[#0F172A] group-hover:text-[#FF9800] transition-colors">
          {q}
        </span>
        <CaretDown
          size={18}
          weight="bold"
          className={`flex-shrink-0 text-zinc-400 transition-transform duration-200 ${
            open ? "rotate-180 text-[#FF9800]" : ""
          }`}
        />
      </button>
      {open && (
        <div className="pb-5 -mt-1 text-sm sm:text-base text-zinc-600 leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  return (
    <MarketingLayout dataTestId="faq-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF9800]">
          Frequently asked
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mt-3 leading-tight">
          Got questions? We've got answers.
        </h1>
        <p className="mt-5 text-lg text-zinc-600 leading-relaxed">
          Can't find what you're looking for?{" "}
          <Link to="/contact" className="text-[#FF9800] underline">
            Drop us a line
          </Link>{" "}
          — we read everything.
        </p>

        <div className="mt-12 space-y-10">
          {FAQ_GROUPS.map((grp, gi) => (
            <section key={grp.title}>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-3">
                {grp.title}
              </div>
              <div className="border-2 border-zinc-200 bg-white px-5 sm:px-7">
                {grp.qs.map((item, qi) => (
                  <FaqItem
                    key={item.q}
                    q={item.q}
                    a={item.a}
                    defaultOpen={gi === 0 && qi === 0}
                    testId={`faq-item-${gi}-${qi}`}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 bg-[#1A2332] text-white p-8 sm:p-10">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF9800] mb-3">
            Still curious?
          </div>
          <div className="font-display font-bold text-xl sm:text-2xl mb-4">
            Try ezItemizer free for 14 days.
          </div>
          <Link
            to="/#pricing"
            data-testid="faq-cta-pricing"
            className="inline-flex items-center gap-2 bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332] font-bold text-xs uppercase tracking-widest px-5 py-3 transition-colors"
          >
            See plans
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </MarketingLayout>
  );
}
